import cx from 'classnames';
import React, { CSSProperties, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';

import styles from './Tooltip.module.scss';

export type Trigger = 'hover' | 'click' | 'focus' | 'disabled';

export interface Props {
  block?: boolean;
  children?: React.ReactNode;
  className?: Parameters<typeof cx>[0];
  clickable?: boolean;
  dark?: boolean;
  id?: string;
  isOpen?: boolean;
  placement: Placement;
  showOn: Trigger | Trigger[];
  small?: boolean;
  style?: CSSProperties;
  target: React.ReactElement;
  wrapperClassName?: Parameters<typeof cx>[0];
  triggerWrapperClassName?: Parameters<typeof cx>[0];

  // https://popper.js.org/docs/v2/modifiers/offset/
  offset?: [number, number];
  onShow?: () => void;
  onHide?: () => void;
  onEscape?: () => void;
}

interface WrapperProps {
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}

export const Tooltip: React.FC<Props> = props => {
  const [targetElement, setTargetElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [tooltipElement, setTooltipElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const popper = usePopper(targetElement, tooltipElement, {
    placement: props.placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          // https://popper.js.org/docs/v2/modifiers/offset/
          offset: props.offset || [0, 10],
        },
      },
      { name: 'arrow', options: { element: arrowElement, padding: 12 } },
    ],
  });

  const [show, setShow] = React.useState(Boolean(props.isOpen));

  const showTooltip = () => {
    if (!show) {
      props.onShow && props.onShow();
      setShow(true);
    }
  };
  const hideTooltip = () => {
    if (show) {
      props.onHide && props.onHide();
      setShow(false);
    }
  };

  const toggleShowTooltip = () => {
    if (show) {
      hideTooltip();
    } else {
      showTooltip();
    }
  };

  useEffect(() => {
    if (props.showOn === 'disabled') {
      return;
    }

    const onTooltipMouseEnter = () => showTooltip();
    const onTooltipMouseLeave = () => hideTooltip();

    if (props.clickable && tooltipElement) {
      tooltipElement.addEventListener('mouseenter', onTooltipMouseEnter);
      tooltipElement.addEventListener('mouseleave', onTooltipMouseLeave);
    }

    /**
     * Buggy: this is triggered when interactive elements in the Tooltip are clicked as well because
     * event.currentTarget is always `document`
     */
    const clickOutsideHandler = (event: MouseEvent) => {
      if (
        tooltipElement &&
        event.target instanceof Node &&
        !tooltipElement.contains(event.target)
      ) {
        props.onEscape && props.onEscape();
        hideTooltip();
      }
    };

    const escapeKeyHandler = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        props.onEscape && props.onEscape();
        hideTooltip();
      }
    };

    // For tooltips that show/hide on target click, also handle escaping via outside click or escape key
    if (props.showOn === 'click') {
      document.addEventListener('click', clickOutsideHandler);
      document.addEventListener('keyup', escapeKeyHandler);
    }

    return () => {
      if (props.clickable && tooltipElement) {
        tooltipElement.removeEventListener('mouseenter', onTooltipMouseEnter);
        tooltipElement.removeEventListener('mouseleave', onTooltipMouseLeave);
      }

      if (props.showOn === 'click') {
        document.removeEventListener('click', clickOutsideHandler);
        document.removeEventListener('keyup', escapeKeyHandler);
      }
    };
  });

  const wrapperProps: WrapperProps = {};

  [props.showOn].flat().forEach((trigger: Trigger) => {
    switch (trigger) {
      case 'click':
        wrapperProps.onClick = toggleShowTooltip;
        break;
      case 'focus':
        wrapperProps.onFocus = showTooltip;
        wrapperProps.onBlur = hideTooltip;
        break;
      case 'hover':
        wrapperProps.onPointerEnter = showTooltip;
        wrapperProps.onPointerLeave = hideTooltip;
    }
  });

  return (
    <div className={cx(styles.tooltipWrapper, props.wrapperClassName)}>
      <div
        className={cx(
          styles.triggerWrapper,
          props.triggerWrapperClassName,
          props.block && styles.block,
        )}
        ref={setTargetElement}
        {...wrapperProps}
      >
        {props.target}
      </div>
      <div
        style={popper.styles.popper}
        className={cx(
          styles.tooltip,
          props.small && styles.small,
          props.dark && styles.dark,
          props.showOn !== 'click' &&
            props.clickable &&
            styles.tooltipClickable,
          show && styles.showTooltip,
          props.className,
        )}
        role='tooltip'
        {...popper.attributes.popper}
        ref={setTooltipElement}
      >
        {typeof props.children === 'string' ? (
          <span className={styles.text}>{props.children}</span>
        ) : (
          props.children
        )}
        <div
          style={popper.styles.arrow}
          className={styles.arrow}
          ref={setArrowElement}
        />
      </div>
    </div>
  );
};

Tooltip.displayName = 'Tooltip';
export default Tooltip;
export { styles };
